import { useState, useEffect } from 'react'
import { Box, Grid2, Paper, Typography, TextField, MenuItem } from "@mui/material";
import { usePageWrapper } from '../../../hooks';
import { ArtButton, ArtPhoneInput, ArtTextField, useApiService } from "@art/shared-ui";
import { AttributePermissionGate } from "../../permissions";
import { GetCountries, GetState } from "react-country-state-city";
import { isValidPhoneNumber } from "react-phone-number-input";
import styles from './billing-settings.module.scss'

const initialAddressState = {
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
    contact: ""
}

const ArtBillingPreferences = () => {

    const apiservice = useApiService();
    const [gstNumber, setGstNumber] = useState("");
    const [primaryAddress, setPrimaryAddress] = useState(initialAddressState);
    const [countries, setCountries] = useState<any[]>([]);
    const [states, setStates] = useState<any[]>([]);
    const [errors, setErrors] = useState<any>({});

    const [initialGstNumber, setInitialGstNumber] = useState("");
    const [initialAddressData, setInitialAddressData] = useState(initialAddressState);

    useEffect(() => {
        GetCountries().then((data: any) => setCountries(data));
    }, []);

    useEffect(() => {
        if (!primaryAddress.country) {
            setStates([]);
            return;
        }
        const country = countries.find((c) => c.name === primaryAddress.country);
        if (country) {
            GetState(country.id).then((data: any) => setStates(data));
        } else {
            setStates([]);
        }
    }, [primaryAddress.country, countries]);

    const handlePrimaryAddressChange = (field: string, value: string) => {
        setPrimaryAddress(prev => {
            const updated = { ...prev, [field]: value };
            if (field === "country") {
                updated.state = "";
            }
            return updated;
        });
        setErrors((prev: any) => ({ ...prev, [field]: "" }));
    };

    const hasChanges = (a: any, b: any) => {
        return Object.keys(a).some(key => a[key] !== b[key]);
    };

    const isModified = gstNumber !== initialGstNumber || hasChanges(primaryAddress, initialAddressData);

    const validate = () => {
        const newErrors: any = {};
        if (!primaryAddress.line1) newErrors.line1 = "Address is required";
        if (!primaryAddress.city) newErrors.city = "City is required";
        if (!primaryAddress.state) newErrors.state = "State is required";
        if (!primaryAddress.country) newErrors.country = "Country is required";
        if (!primaryAddress.postal_code) newErrors.postal_code = "Pincode is required";
        if (!primaryAddress.contact) {
            newErrors.contact = "Contact number is required";
        } else if (!isValidPhoneNumber(primaryAddress.contact)) {
            newErrors.contact = "Enter a valid contact number";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const saveBillingAddress = async () => {
        if (!validate()) return;

        try {
            const response = await apiservice.put("/organisation/billing-address", primaryAddress);
            if (response?.status === 200 || response?.status === 201) {
                if (gstNumber.trim() !== initialGstNumber) {
                    try {
                        await apiservice.put("/organisation/gst-number", { gst_number: gstNumber.trim() });
                    } catch (gstError) {
                        console.error("Error saving GST number:", gstError);
                    }
                }
                apiservice.showSnack(response.data?.message || "Billing details updated successfully", "success")
                fetchBillingDetails();
            }
        } catch (error) {
            console.error("Error saving address details: ", error)
            apiservice.showSnack("Failed to save billing details", "error")
        }

    }

    const fetchBillingDetails = async () => {
        try {
            const [addressResponse, gstResponse] = await Promise.all([
                apiservice.get("/organisation/billing-address"),
                apiservice.get("/organisation/gst-number", { showErrorSnack: false }),
            ]);

            if (addressResponse?.status === 200 && addressResponse.data?.data) {
                const address = addressResponse.data.data;
                const normalizedAddress = {
                    line1: address.line1 || "",
                    line2: address.line2 || "",
                    city: address.city || "",
                    state: address.state || "",
                    postal_code: address.postal_code || "",
                    country: address.country || "",
                    contact: address.contact || "",
                };
                setPrimaryAddress(normalizedAddress);
                setInitialAddressData(normalizedAddress);
            }

            if (gstResponse?.status === 200) {
                const fetchedGst = gstResponse.data?.data?.gst_number || "";
                setGstNumber(fetchedGst);
                setInitialGstNumber(fetchedGst);
            }
        } catch (error) {
            console.error("Failed to fetch billing details", error);
        }
    };

    useEffect(() => {
        fetchBillingDetails();
    }, []);

    return (
        <div>
            <Typography variant="h4">Billing Information Preferences</Typography>
            <Typography variant="caption" color="text.secondary" >Manage your company's billing identity and address details. These settings are used for generating invoices, sending billing notifications, and calculating applicable taxes.</Typography>

            <Box mb={2} mt={3}>
                <Grid2 container spacing={5} mb={2}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Paper elevation={0} className={styles.billing_card} >
                            <Box mb={2}>
                                <Typography variant="subtitle1" color="textSecondary" ><b>Business tax ID</b></Typography>
                                <Typography variant="subtitle2" color="textDisabled" mb={2}>If you are a business tax registrant, please enter your business tax ID here.</Typography>
                                <ArtTextField
                                    label="Tax ID / GST Number"
                                    value={gstNumber}
                                    onChange={(e: any) => setGstNumber(e.target.value)}
                                    marginBottom={18}
                                />
                            </Box>
                        </Paper>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Paper elevation={0} className={styles.billing_card} >

                            <Typography variant="subtitle1" color="textSecondary" ><b>Primary business address</b></Typography>
                            <Typography variant="subtitle2" color="textDisabled" mb={2}>This is the physical address of the company purchasing ART services and is used to calculate any applicable sales tax.</Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                                <ArtTextField
                                    label="Address Line 1"
                                    value={primaryAddress.line1}
                                    onChange={(e: any) => handlePrimaryAddressChange("line1", e.target.value)}
                                    marginBottom={0}
                                    error={errors.line1}
                                    multiline
                                />
                                <ArtTextField
                                    label="Address Line 2 (Optional)"
                                    value={primaryAddress.line2}
                                    onChange={(e: any) => handlePrimaryAddressChange("line2", e.target.value)}
                                    marginBottom={0}
                                />
                                <Grid2 container spacing={2}>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <ArtTextField
                                            label="City"
                                            value={primaryAddress.city}
                                            onChange={(e: any) => handlePrimaryAddressChange("city", e.target.value)}
                                            marginBottom={0}
                                            error={errors.city}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Country"
                                            value={primaryAddress.country}
                                            onChange={(e: any) => handlePrimaryAddressChange("country", e.target.value)}
                                            error={!!errors.country}
                                            helperText={errors.country}
                                        >
                                            {countries.map((c) => (
                                                <MenuItem key={c.id} value={c.name}>
                                                    {c.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="State / Province"
                                            value={primaryAddress.state}
                                            onChange={(e: any) => handlePrimaryAddressChange("state", e.target.value)}
                                            error={!!errors.state}
                                            helperText={errors.state}
                                            disabled={!primaryAddress.country || states.length === 0}
                                        >
                                            {states.map((s) => (
                                                <MenuItem key={s.id} value={s.name}>
                                                    {s.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <ArtTextField
                                            label="Pincode"
                                            value={primaryAddress.postal_code}
                                            onChange={(e: any) => {
                                                const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                                                handlePrimaryAddressChange("postal_code", val);
                                            }}
                                            marginBottom={0}
                                            error={errors.postal_code}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12 }}>
                                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                                            Contact Number
                                        </Typography>
                                        <ArtPhoneInput
                                            defaultCountry="IN"
                                            placeholder="Enter phone number"
                                            value={primaryAddress.contact}
                                            onChange={(val) => handlePrimaryAddressChange("contact", val || "")}
                                            error={!!errors.contact}
                                        />
                                        {errors.contact && (
                                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                                {errors.contact}
                                            </Typography>
                                        )}
                                    </Grid2>
                                </Grid2>

                            </Box>

                        </Paper>
                    </Grid2>

                </Grid2>
                {isModified && (
                    <AttributePermissionGate application="PLATFORM_SETTINGS" feature="BILLING_USAGE" attribute="ManageBillingDetails">
                        <ArtButton label='Update' onClick={saveBillingAddress}/>
                    </AttributePermissionGate>
                )}

            </Box>


        </div>
    )
}

export default usePageWrapper(ArtBillingPreferences, {
    page_title: "Preferences"
});
